import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import WebhookIcon from '@mui/icons-material/Webhook';
import WebhookDeliveryLog from './WebhookDeliveryLog';
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useFirestoreQuery } from '../../hooks/useFirestoreQuery';
import { formatApiKeyDate } from '../../lib/apiKeys';
import { generateWebhookSigningSecret } from '../../lib/webhookSecrets';

const WEBHOOK_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
};

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isValidHttpsWebhookUrl(url) {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (!trimmed.startsWith('https://')) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * @param {string | undefined} status
 * @returns {boolean}
 */
function isWebhookActive(status) {
  return status === WEBHOOK_STATUS.ACTIVE;
}

/**
 * Company Admin UI for tenant outbound webhook endpoints.
 * @param {{ userData?: { companyId?: string, role?: string } }} props
 */
export default function WebhookManager({ userData }) {
  const { companyId: workspaceCompanyId, loading: workspaceLoading } = useWorkspace();
  const isCompanyAdmin = userData?.role === 'company_admin';
  const resolvedCompanyId = workspaceCompanyId || userData?.companyId;

  const webhooksQuery = useMemo(() => {
    if (!resolvedCompanyId || !isCompanyAdmin) return null;
    return collection(db, 'companies', resolvedCompanyId, 'outbound_webhooks');
  }, [resolvedCompanyId, isCompanyAdmin]);

  const { data: webhooksRaw, loading: webhooksLoading, error: webhooksError } =
    useFirestoreQuery(webhooksQuery, {
      enabled: Boolean(resolvedCompanyId && isCompanyAdmin),
    });

  const webhooks = useMemo(() => {
    const rows = [...webhooksRaw];
    rows.sort((a, b) => {
      const ad = a.createdAt?.toDate?.() ? a.createdAt.toDate().getTime() : 0;
      const bd = b.createdAt?.toDate?.() ? b.createdAt.toDate().getTime() : 0;
      return bd - ad;
    });
    return rows;
  }, [webhooksRaw]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [targetUrl, setTargetUrl] = useState('');
  const [statusActive, setStatusActive] = useState(true);
  const [urlError, setUrlError] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [signingSecret, setSigningSecret] = useState('');
  const [copiedSecretFor, setCopiedSecretFor] = useState('');

  if (!isCompanyAdmin) {
    return null;
  }

  if (!resolvedCompanyId) {
    return (
      <Alert severity="info" sx={{ mt: 3, maxWidth: 720 }}>
        Link your account to a company workspace to configure outbound webhooks.
      </Alert>
    );
  }

  const openCreateDialog = () => {
    setEditingId(null);
    setTargetUrl('');
    setStatusActive(true);
    setSigningSecret(generateWebhookSigningSecret());
    setUrlError('');
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (row) => {
    setEditingId(row.id);
    setTargetUrl(row.targetUrl || '');
    setStatusActive(isWebhookActive(row.status));
    setSigningSecret(row.signingSecret || '');
    setUrlError('');
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const handleSave = async () => {
    const trimmedUrl = targetUrl.trim();
    if (!isValidHttpsWebhookUrl(trimmedUrl)) {
      setUrlError('Enter a secure HTTPS URL (must start with https://).');
      return;
    }

    if (!resolvedCompanyId) return;

    setSaving(true);
    setFormError('');
    setUrlError('');

    const status = statusActive ? WEBHOOK_STATUS.ACTIVE : WEBHOOK_STATUS.INACTIVE;
    const actorUid = auth.currentUser?.uid || null;

    try {
      if (editingId) {
        const patch = {
          targetUrl: trimmedUrl,
          status,
          updatedAt: serverTimestamp(),
          updatedByUid: actorUid,
        };
        if (!signingSecret) {
          patch.signingSecret = generateWebhookSigningSecret();
        }
        await updateDoc(
          doc(db, 'companies', resolvedCompanyId, 'outbound_webhooks', editingId),
          patch,
        );
      } else {
        await addDoc(collection(db, 'companies', resolvedCompanyId, 'outbound_webhooks'), {
          targetUrl: trimmedUrl,
          status,
          signingSecret: signingSecret || generateWebhookSigningSecret(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdByUid: actorUid,
        });
      }
      setDialogOpen(false);
      setEditingId(null);
      setTargetUrl('');
      setStatusActive(true);
      setSigningSecret('');
    } catch (e) {
      console.error('WebhookManager save:', e);
      setFormError(e.message || 'Could not save webhook.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopySigningSecret = async (secret, rowId) => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedSecretFor(rowId || 'dialog');
      setTimeout(() => setCopiedSecretFor(''), 2000);
    } catch (e) {
      console.error('copy signing secret:', e);
      setFormError('Could not copy signing secret to clipboard.');
    }
  };

  const renderSigningSecretField = (secret, copyKey) => (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Signing secret
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Verify requests with the <code>X-Shifty-Signature</code> header — HMAC SHA-256 hex
        digest of the JSON <code>payload</code> field, signed with this secret.
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          fullWidth
          size="small"
          value={secret || 'Save this webhook to generate a signing secret.'}
          InputProps={{ readOnly: true }}
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '0.8rem',
            },
          }}
        />
        <Tooltip title={copiedSecretFor === copyKey ? 'Copied!' : 'Copy signing secret'}>
          <span>
            <IconButton
              size="small"
              onClick={() => handleCopySigningSecret(secret, copyKey)}
              disabled={!secret}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <WebhookIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Outbound Webhooks
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Receive real-time ticket events at external HTTPS endpoints (e.g. Slack, Zapier).
              </Typography>
            </Box>
          </Stack>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Add Webhook
          </Button>
        </Stack>

        {webhooksError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {webhooksError.message || 'Could not load webhooks.'}
          </Alert>
        )}

        {workspaceLoading || webhooksLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : webhooks.length === 0 ? (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            No outbound webhooks configured. Add one to notify external systems when tickets are
            created or updated.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Target URL</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {webhooks.map((row) => {
                  const active = isWebhookActive(row.status);
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow hover>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              wordBreak: 'break-all',
                            }}
                          >
                            {row.targetUrl || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={active ? 'Active' : 'Inactive'}
                            color={active ? 'success' : 'default'}
                            variant={active ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>{formatApiKeyDate(row.createdAt)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit webhook">
                            <IconButton size="small" onClick={() => openEditDialog(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            py: 1.5,
                            px: 2,
                            bgcolor: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.03)'
                                : 'grey.50',
                            borderBottom: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Stack spacing={1.5}>
                            {renderSigningSecretField(row.signingSecret, row.id)}
                            <WebhookDeliveryLog webhookId={row.id} />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit webhook' : 'Add webhook'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError('')}>
                {formError}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              label="Target URL"
              placeholder="https://hooks.example.com/your-endpoint"
              value={targetUrl}
              onChange={(e) => {
                setTargetUrl(e.target.value);
                setUrlError('');
              }}
              error={Boolean(urlError)}
              helperText={
                urlError || 'HTTPS only — payloads are sent as POST requests with ticket event data.'
              }
              disabled={saving}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={statusActive}
                  onChange={(e) => setStatusActive(e.target.checked)}
                  disabled={saving}
                />
              )}
              label={statusActive ? 'Active' : 'Inactive'}
            />
            {editingId ? (
              renderSigningSecretField(
                signingSecret,
                'dialog',
              )
            ) : (
              renderSigningSecretField(signingSecret, 'dialog-new')
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
